'use client'

import { useEffect, useId, useRef, useState } from 'react'
import { ChevronDown } from 'lucide-react'
import gsap from 'gsap'
import { useGSAP } from '@gsap/react'

import RichText from '/react4xp/components/prototype/RichText'

import type { FaqItem, FaqProps } from './types'

function FaqRow({ question, answer, open = false }: FaqItem) {
	const [isOpen, setIsOpen] = useState(open)
	const rowRef = useRef<HTMLDivElement>(null)
	const panelRef = useRef<HTMLDivElement>(null)
	const triggerRef = useRef<HTMLButtonElement>(null)
	const panelId = useId()

	function close() {
		setIsOpen(false)
		triggerRef.current?.focus()
	}

	useEffect(() => {
		function handleKeyDown(event: KeyboardEvent) {
			if (event.key !== 'Escape' || !isOpen) return
			if (!rowRef.current?.contains(document.activeElement)) return
			close()
		}
		document.addEventListener('keydown', handleKeyDown)
		return () => document.removeEventListener('keydown', handleKeyDown)
	}, [isOpen])

	useGSAP(
		() => {
			const el = panelRef.current
			if (!el) return
			gsap.killTweensOf(el)

			if (isOpen) {
				gsap.set(el, { height: 'auto' })
				const target = el.offsetHeight
				gsap.fromTo(
					el,
					{ height: 0 },
					{
						height: target,
						duration: 0.3,
						ease: 'power2.inOut',
						onComplete: () => gsap.set(el, { height: 'auto' }),
					},
				)
			} else {
				gsap.to(el, {
					height: 0,
					duration: 0.3,
					ease: 'power2.inOut',
				})
			}
		},
		{ dependencies: [isOpen] },
	)

	return (
		<div ref={rowRef} className="border-b border-black-100">
			<button
				ref={triggerRef}
				type="button"
				aria-expanded={isOpen}
				aria-controls={panelId}
				onClick={() => setIsOpen((prev) => !prev)}
				className="flex w-full items-center justify-between gap-6 p-3 text-left focus-visible:outline-2 focus-visible:outline-black-950"
			>
				<span className="h4 text-black-950">{question}</span>
				<span
					className={
						isOpen
							? 'flex size-11 shrink-0 items-center justify-center rounded-full bg-black-200'
							: 'flex size-11 shrink-0 items-center justify-center rounded-full bg-black-100 hover:bg-black-200'
					}
				>
					<ChevronDown
						className={isOpen ? 'size-5 rotate-180 transition-transform' : 'size-5 transition-transform'}
						aria-hidden="true"
					/>
				</span>
			</button>

			<div
				id={panelId}
				ref={panelRef}
				tabIndex={-1}
				aria-hidden={!isOpen}
				{...(!isOpen ? ({ inert: '' } as Record<string, string>) : {})}
				className="h-0 overflow-hidden focus:outline-none"
			>
				<div className="px-3 pb-6">
					<RichText>{answer}</RichText>
				</div>
			</div>
		</div>
	)
}

export default function Faq({ heading = undefined, items = [] }: FaqProps) {
	if (items.length === 0) return null

	const list = (
		<div className="flex flex-col lg:min-w-[calc(50%-1rem)] lg:flex-1">
			{items.map((item, i) => (
				<FaqRow key={i} question={item.question} answer={item.answer} open={item.open} />
			))}
		</div>
	)

	return (
		<section className="container my-16">
			{heading ? (
				<div className="flex flex-col gap-8 lg:flex-row">
					<div className="lg:min-w-[calc(50%-1rem)] lg:flex-1">
						<h2 className="h3 text-black-950">{heading}</h2>
					</div>
					{list}
				</div>
			) : (
				list
			)}
		</section>
	)
}
